
// httpconfig.interceptor.ts
import { Observable, from, of, throwError, firstValueFrom, lastValueFrom } from 'rxjs';
import { Injectable } from '@angular/core';
import {
  HttpInterceptor, HttpRequest, HttpHandler, HttpEvent,
  HttpHeaders, HttpErrorResponse, HttpResponse
} from '@angular/common/http';
import { MsalService } from '@azure/msal-angular';
import { catchError, map } from 'rxjs/operators';
import { Router } from '@angular/router';
import { HttpError } from '../models/httperror';
import { environment } from 'src/environments/environment';
import { LOCAL_STORAGE_KEY, AUTHORIZATION_SCHEME } from '../models/constants';

@Injectable()
export class HttpConfigInterceptor implements HttpInterceptor {

  constructor(
    private msalService: MsalService,
    private router: Router
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Add common headers for non-Graph requests
    if (!request.url.startsWith(environment.graphUrl)) {
      request = request.clone({
        withCredentials: true,
        setHeaders: {
          'Strict-Transport-Security': 'max-age=31536000; includeSubDomains; preload'
        }
      });
    }

    return from(this.handleAccess(request, next)).pipe(
      map(response => this.handleResponse(response)),
      catchError(x => this.handleAuthError(x))
    );
  }

  private async handleAccess(request: HttpRequest<any>, next: HttpHandler): Promise<HttpEvent<any>> {
    let changedRequest = request;
    const headerSettings: { [name: string]: string | string[] } = {};

    // Copy existing headers
    for (const key of request.headers.keys()) {
      headerSettings[key] = request.headers.getAll(key)!;
    }

    const activeAccount = this.msalService.instance.getActiveAccount();

    // === Branch 1: Microsoft Graph ===
    if (request.url.startsWith(environment.graphUrl)) {
      // Only attempt MSAL if we have an account
      if (activeAccount) {
        try {
          const tokenResponse = await firstValueFrom(
            this.msalService.acquireTokenSilent({
              scopes: ['User.Read'],            // ✅ correct casing
              account: activeAccount
            })
          );
          headerSettings.Authorization = AUTHORIZATION_SCHEME.BEARER + tokenResponse.accessToken; // ✅ accessToken
        } catch (err) {
          // ❌ DO NOT trigger login here — guard will handle auth.
          // Log and proceed without Authorization; server may return 401 and route guards can handle it.
          console.warn('[Interceptor] acquireTokenSilent for Graph failed:', err);
        }
      }
    }
    // === Branch 2: Your own API ===
    else if (request.url.startsWith(environment.apiUrl)) {
      // Prefer your existing DMA token if valid
      const dmaToken = localStorage.getItem(LOCAL_STORAGE_KEY.TOKEN);
      const expiresIn = Number(localStorage.getItem(LOCAL_STORAGE_KEY.EXPIRES_IN)) * 1000;

      if (dmaToken && expiresIn && Date.now() < expiresIn) {
        headerSettings.Authorization = AUTHORIZATION_SCHEME.BASIC + dmaToken;
      } else {
        // Try MSAL access token for your API only if we have an account
        if (activeAccount) {
          try {
            const tokenResponse = await firstValueFrom(
              this.msalService.acquireTokenSilent({
                scopes: [environment.apiScope],     // <-- add this in environment.ts
                account: activeAccount
              })
            );
            headerSettings.Authorization = AUTHORIZATION_SCHEME.BEARER + tokenResponse.accessToken; // ✅ accessToken
          } catch (err) {
            // ❌ DO NOT call loginRedirect here
            console.warn('[Interceptor] acquireTokenSilent for API failed:', err);
          }
        }
      }
    }

    // Keep this only if your backend uses cookies; for pure Bearer flows you can drop it
    if (!request.url.startsWith('https://graph.microsoft.com')) {
        headerSettings['Strict-Transport-Security'] = 'max-age=31536000; includeSubDomains; preload';
    }

    // Always add clickjacking header
    headerSettings['X-Frame-Options'] = 'SAMEORIGIN';

    const newHeader = new HttpHeaders(headerSettings);
    changedRequest = request.clone({ headers: newHeader });

    return lastValueFrom(next.handle(changedRequest));
  }


private handleResponse(response: any) {
  // 🔒 If the response body is a Blob (file download), do NOT touch it
  if (response instanceof HttpResponse && response.body instanceof Blob) {
    return response; // leave blob responses intact
  }

  // Existing token-envelope logic for non-Graph JSON:
  if (
    response instanceof HttpResponse &&
    !response.url.startsWith(environment.graphUrl) &&
    response.body &&
    typeof response.body === 'object' &&
    response.body.hasOwnProperty('token')
  ) {
    const existingToken = localStorage.getItem(LOCAL_STORAGE_KEY.TOKEN);

    if (response.body.token !== existingToken) {
      localStorage.setItem(LOCAL_STORAGE_KEY.TOKEN, response.body.token);
      localStorage.setItem(LOCAL_STORAGE_KEY.EXPIRES_IN, response.body.expiresIn);
      const body = response.body.data;
      response = response.clone({ body });
    }
  }
   return response;
}

  private handleAuthError(err: HttpErrorResponse): Observable<never> {
  const logFormat = 'background: maroon; color: white';

  // Build a readable dev log message (optional)
  const errorMessage =
    err.error instanceof ErrorEvent
      ? `Error: ${err.error.message}`
      : `Error Code: ${err.status}\nMessage: ${err.message}`;

  if (err instanceof HttpErrorResponse) {
    switch (err.status) {
      case HttpError.Unauthorized: // 401
        this.router.navigateByUrl(`/unauthorized`);
        if (!environment.production) {
          console.error('%c Unauthorized [401]', logFormat);
        }
        // Swallow after navigation (no need to propagate further)
        return throwError(() => err);

      case HttpError.NotFound: // 404
        // Avoid navigating for Graph 404s
        if (!err.url?.startsWith(environment.graphUrl)) {
          this.router.navigateByUrl(`/notfound`);
        }
        if (!environment.production) {
          console.error('%c Not Found [404]', logFormat);
        }
        return throwError(() => err);

      default:
        // ✅ Only redirect for 5xx (real server errors)
        if (err.status >= 500) {
          this.router.navigateByUrl(`/error`);
          if (!environment.production) {
            console.error(`%c Internal Server Error [${err.status}]`, logFormat);
          }
          // Re-throw the original HttpErrorResponse (optional);
          // you can also return EMPTY if you want to completely swallow it.
          return throwError(() => err);
        }

        // ✅ For 4xx (e.g., 400/403/409/422), DO NOT redirect.
        // Let the component handle it and read the JSON body.
        if (!environment.production) {
          console.warn(`%c Client Error [${err.status}] → passing to component`, logFormat, err);
        }
        return throwError(() => err);
    }
  }
    // Fallback — propagate the original error object
    return throwError(() => err);
  }
}